import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Check, X, Trash2, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const FILTERS = ["", "pending", "approved", "spam"];
const STYLES = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800",
  spam: "bg-red-100 text-red-700",
};

export default function AdminComments() {
  const [comments, setComments] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/comments", { params: filter ? { status: filter } : {} })
      .then((r) => setComments(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/comments/${id}`, { status });
      toast.success(`Comment ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/admin/comments/${id}`);
      toast.success("Comment deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-comments-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-brand-emerald">Comment Moderation</h1>
          <p className="text-brand-muted text-sm mt-1">Approve the good, bin the spam.</p>
        </div>
        <div className="flex gap-2">
          {FILTERS.map((f) => (
            <button
              key={f || "all"}
              data-testid={`comments-filter-${f || "all"}`}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-xs font-bold transition-colors ${filter === f ? "bg-brand-emerald text-white" : "bg-white text-brand-muted border border-[#D6E4DC]"}`}
            >
              {f || "All"}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-10 h-10 border-4 border-brand-emerald border-t-brand-lime rounded-full animate-spin" /></div>
      ) : comments.length === 0 ? (
        <div className="bg-brand-mint rounded-2xl border border-dashed border-brand-emerald/20 p-12 text-center text-brand-muted" data-testid="comments-empty">No {filter} comments right now.</div>
      ) : (
        <div className="space-y-3">
          {comments.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl border border-[#D6E4DC] p-5 flex flex-col md:flex-row md:items-start gap-4" data-testid={`comment-row-${c.id}`}>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-1.5">
                  <span className="font-bold text-sm text-brand-ink">{c.name}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STYLES[c.status]}`}>{c.status}</span>
                  {c.status === "spam" && <ShieldAlert className="w-3.5 h-3.5 text-red-500" />}
                </div>
                <p className="text-xs text-brand-muted mb-2">on <strong>{c.post_title}</strong> · {new Date(c.created_at).toLocaleString("en-NG")}</p>
                <p className="text-sm text-brand-ink leading-relaxed">{c.content}</p>
              </div>
              <div className="flex md:flex-col gap-2">
                {c.status !== "approved" && (
                  <button onClick={() => setStatus(c.id, "approved")} data-testid={`comment-approve-${c.id}`} className="px-3 py-1.5 rounded-lg bg-green-100 text-green-800 text-xs font-bold hover:bg-green-200 flex items-center gap-1 transition-colors"><Check className="w-3.5 h-3.5" /> Approve</button>
                )}
                {c.status !== "spam" && (
                  <button onClick={() => setStatus(c.id, "spam")} data-testid={`comment-spam-${c.id}`} className="px-3 py-1.5 rounded-lg bg-amber-100 text-amber-800 text-xs font-bold hover:bg-amber-200 flex items-center gap-1 transition-colors"><X className="w-3.5 h-3.5" /> Spam</button>
                )}
                <button onClick={() => remove(c.id)} data-testid={`comment-delete-${c.id}`} className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 text-xs font-bold hover:bg-red-100 flex items-center gap-1 transition-colors"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
